import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { loadConfig } from './config.js';
import { waitForHandshake, type VarioHandshake } from './vario-handshake.js';
import { exchangeAppToken, AuthBridgeFailure, type AuthBridgeSession } from './auth-bridge-client.js';
import { renderInstall } from './views/install.js';
import { renderDashboard } from './views/dashboard.js';
import { renderUninstall } from './views/uninstall.js';
import { renderListingStatus } from './views/listing-status.js';
import { renderLog } from './views/tabs/log.js';
import { renderOrders } from './views/tabs/orders.js';
import { renderOffers } from './views/tabs/offers.js';
import { renderOffersImport } from './views/tabs/offers-import.js';
import { renderOttoSettings } from './views/otto-settings.js';
import { renderOttoCredentials } from './views/otto-credentials.js';
import { renderOttoList } from './views/tabs/otto-list.js';
import { renderMcp } from './views/tabs/mcp.js';
import { initVarioIframeHeight, signalVarioReady } from './vario-iframe.js';
import { watchSessionExpiry } from './lib/session-expiry.js';

// Bridge-Layer: aliased die kl-*/v-*-Klassen der noch-nicht-migrierten Tabs
// (orders, offers, lister) auf das Brand-Design ohne Markup-Aenderung. MUSS
// als allerletzter CSS-Import laden, damit die Aliase Specificity-Gewinn
// haben gegen die Legacy-Regeln in patterns.css.
import '@vario-connect/frontend-design/bridge.css';

const trace = (...args: unknown[]) => console.log('[otto-app]', ...args);
const traceErr = (...args: unknown[]) => console.error('[otto-app]', ...args);

type Slot = 'install' | 'uninstall' | 'dashboard' | 'settings' | 'credentials' | 'listing-status' | 'log' | 'orders' | 'offers' | 'offers-import' | 'list' | 'mcp';

function readSlot(): Slot {
  const hash = window.location.hash.replace(/^#\/?/, '').toLowerCase();
  if (
    hash === 'install' || hash === 'uninstall' || hash === 'settings' ||
    hash === 'credentials' ||
    hash === 'listing-status' || hash === 'log' ||
    hash === 'orders' || hash === 'offers' || hash === 'offers-import' || hash === 'list' ||
    hash === 'mcp'
  ) {
    return hash;
  }
  return 'dashboard';
}

/** Direkt im Browser geöffnet: kein Token in der URL und kein umgebendes VARIO-iframe. */
function isOutsideVario(): boolean {
  if (new URLSearchParams(window.location.search).get('appToken')) return false;
  if (new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token')) return false;
  return window.parent === window;
}

async function bootstrap() {
  const outsideVario = isOutsideVario();

  // iframe-Höhe je Slot:
  //   - listing-status: FESTES Fenster (LISTING_STATUS_HEIGHT), der Slot-Inhalt
  //     scrollt intern — der Vario-Artikel-Bereich ist kuerzer als lange Tabs
  //     (Mapping/Bilder/Decide), bei mitwachsendem iframe waere der untere Teil
  //     unerreichbar.
  //   - install / uninstall / credentials: kompakter Inhalt, waechst mit dem
  //     Inhalt (auto). WICHTIG: install/uninstall NICHT 'fixed' 900px — sonst
  //     ist der iframe viel zu hoch und Varios OK-Button liegt weit unter dem
  //     Karten-Inhalt (langes Scrollen). Auto klemmt auf [AUTO_MIN, AUTO_MAX].
  //   - sonst (dashboard, log, mcp): fixer Voll-Korridor (FIXED_HEIGHT), Inhalt scrollt intern.
  if (!outsideVario) {
    const slot = readSlot();
    if (slot === 'listing-status') {
      // Auto-Grow wie die Einstellungen: iframe wächst auf die volle Inhaltshöhe (kein Max-Clamp),
      // Vario scrollt außen → das lange Detail-Pane/Mapping ist komplett erreichbar. (Varios neues
      // useFullHeight-Modell greift in dieser Instanz noch nicht → bewusst der bewährte Weg.)
      // Voraussetzung: listing-status setzt html.otto-autogrow (löst die bridge-§16-100%-Kette).
      initVarioIframeHeight('auto', undefined, 540, Infinity);
    } else if (slot === 'settings' || slot === 'offers-import') {
      // Sales-Channel-Einstellungen: reines Framework-Auto-Grow (height.js-Verhalten).
      // iframe = Inhaltshoehe, KEIN interner Scroll, KEIN Max-Clamp (Infinity) — Vario
      // scrollt aussen. Voraussetzung: otto-settings loest die bridge-§16-100%-Kette per
      // <html.otto-autogrow>-Override, sonst spiegelt body.scrollHeight nicht den Inhalt.
      // (Frueher fixer 680er + interner Scroll → beim Vergroessern abgeschnitten/zu hoch.)
      initVarioIframeHeight('auto', undefined, 200, Infinity);
    } else if (
      slot === 'install' || slot === 'uninstall' || slot === 'credentials'
    ) {
      initVarioIframeHeight('auto');
    } else {
      initVarioIframeHeight('fixed');
    }
  }

  trace('bootstrap start', {
    href: window.location.href,
    origin: window.location.origin,
    outsideVario,
    isInIframe: window.parent !== window,
    parentOrigin: (() => {
      try { return window.parent !== window ? document.referrer : '(top)'; } catch { return '(crossorigin)'; }
    })(),
  });

  const statusEl = document.querySelector<HTMLParagraphElement>('#status')!;
  const dotEl = document.querySelector<HTMLSpanElement>('#status-dot');
  const contentEl = document.querySelector<HTMLElement>('#content')!;

  const setStatus = (text: string, kind: 'info' | 'ok' | 'error' = 'info') => {
    if (dotEl) { dotEl.className = `status-dot status-dot--${kind}`; dotEl.title = text; }
    if (kind === 'error') {
      statusEl.textContent = text;
      statusEl.className = 'error';
      statusEl.hidden = false;
    } else {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }
    trace('status:', text);
  };

  let config;
  try {
    config = loadConfig();
  } catch (e) {
    setStatus(`Konfiguration unvollständig: ${(e as Error).message}`, 'error');
    return;
  }

  // Die App läuft nur im VARIO-iframe. Direkt im Browser geöffnet gibt es keinen Token, also
  // gleich Bescheid geben statt 15 Sekunden auf den Handshake zu warten.
  if (outsideVario) {
    setStatus('Diese App läuft nur innerhalb von VARIO. Bitte über VARIO öffnen.', 'error');
    return;
  }

  let session: AuthBridgeSession;
  let handshake: VarioHandshake;

  setStatus('Warte auf Vario-Handshake …');
  try {
    handshake = await waitForHandshake(config.allowedHostOrigins);
  } catch (e) {
    setStatus(`Handshake fehlgeschlagen: ${(e as Error).message}`, 'error');
    return;
  }

  setStatus('Tausche Token gegen Supabase-Session …');
  try {
    session = await exchangeAppToken(config.authBridgeUrl, handshake.appToken);
  } catch (e) {
    if (e instanceof AuthBridgeFailure) {
      // Abgelaufener/ungültiger Vario-App-Token (z. B. Fenster lag lange offen, bevor es
      // geöffnet/aktiviert wurde): handlungsleitende statt kryptischer Meldung. Ein iframe-
      // Reload würde denselben (abgelaufenen) `?appToken` aus der URL erneut tauschen →
      // bewusst KEIN Auto-Reload (Endlosschleife). Der frische Token kommt nur, wenn Vario
      // das Fenster neu öffnet; F5 lädt die ganze VARIO-Seite und reicht dafür (Test M7, 27.10.). Server-seitiger Clock-Skew ist via clockTolerance abgefangen.
      if (e.code === 'TOKEN_VERIFY_FAILED') {
        setStatus(
          'Sitzung abgelaufen. Bitte die Seite mit F5 neu laden, dann geht es weiter.',
          'error',
        );
      } else {
        setStatus(`Auth-Bridge ${e.status}: ${e.code} — ${e.message}`, 'error');
      }
    } else {
      setStatus(`Auth-Bridge unerreichbar: ${(e as Error).message}`, 'error');
    }
    return;
  }

  // Vor createClient: supabase-js bindet fetch beim Erzeugen (Testplan M7).
  watchSessionExpiry(session.expiresAt, config.supabaseUrl);

  const supabase: SupabaseClient = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    global: { headers: { Authorization: `Bearer ${session.token}` } },
    auth: { persistSession: false, autoRefreshToken: false },
  });

  setStatus(`Verbunden — Tenant ${session.tenantId.slice(0, 8)}…`, 'ok');

  const slot = readSlot();
  trace('routing to slot', slot);

  // VARIO liefert den Offline-Token im Install-Slot und bei „Offline Token erneuern" als
  // URL-Parameter. Auf jedem Slot abgreifen und speichern; otto_set_offline_token legt die
  // Verbindung bei Bedarf als pending_credentials an (das Otto-OAuth folgt erst danach).
  if (handshake.offlineToken) {
    try {
      const { data, error } = await supabase.rpc('otto_set_offline_token', {
        _token: handshake.offlineToken,
      });
      if (error) traceErr('offline token capture failed', error.message);
      else trace('offline token capture result', data);
    } catch (e) {
      traceErr('offline token capture exception', (e as Error).message);
    }
  }

  const ctx = {
    contentEl,
    supabase,
    session: {
      token: session.token,
      tenantId: session.tenantId,
      marketplaceKind: session.marketplaceKind,
    },
    handshake,
    supabaseUrl: config.supabaseUrl,
  };

  switch (slot) {
    case 'install':
      await renderInstall(ctx);
      break;
    case 'dashboard':
      await renderDashboard(ctx);
      break;
    case 'uninstall':
      await renderUninstall(ctx);
      break;
    case 'listing-status':
      await renderListingStatus({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'log':
      renderLog({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'orders':
      renderOrders({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'offers':
      renderOffers({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'offers-import':
      renderOffersImport({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'list':
      renderOttoList({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'settings':
      renderOttoSettings({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'mcp':
      renderMcp({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'credentials':
      renderOttoCredentials({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
  }

  // VARIO-Ready-Signal: App vollständig geladen und im Vario-iframe erreichbar
  // (Host wertet ab 2026-08-20 aus). Nur im Erfolgsfall; Fehlerpfade
  // erreichen diese Stelle nicht.
  signalVarioReady();
}

bootstrap().catch((err) => {
  traceErr('bootstrap fatal', err);
  const statusEl = document.querySelector<HTMLParagraphElement>('#status');
  if (statusEl) {
    statusEl.textContent = `Bootstrap-Fehler: ${(err as Error).message ?? err}`;
    statusEl.className = 'error';
  }
});

window.addEventListener('error', (e) => traceErr('window error', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => traceErr('unhandled rejection', e.reason));
